import { z } from "zod";
import type { NegativeMatchTypeSchema } from "../campaign-creation";
import {
  SmartCreationAdGroupStableKeySchema,
  SmartCreationCampaignStableKeySchema,
  SmartCreationTargetStableKeySchema,
} from "../smart-creation";

export const ContentHashSchema = z.string().regex(/^[a-f0-9]{64}$/);
export const LineageKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9:_-]+$/);
export const EntityIdSchema = z.string().trim().min(1).max(128);
export const NameSchema = z.string().trim().min(1).max(128);
const NamingTemplateSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .superRefine((template, ctx) => {
    const tokens = new Set(["sku", "asin", "strategy", "match", "adGroup", "counter"]);
    const unresolved = template.replace(/\{([^{}]+)\}/g, (_, token: string) =>
      tokens.has(token) ? "" : `{${token}}`,
    );
    if (/[{}]/.test(unresolved))
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Unknown naming placeholder" });
  });
export const SmartCreationReviewNamingRuleSchema = z
  .object({
    selectedRule: z.enum(["system", "custom"]),
    customTemplate: z
      .object({ campaign: NamingTemplateSchema, adGroup: NamingTemplateSchema })
      .strict()
      .nullable(),
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.selectedRule === "custom" && rule.customTemplate === null)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["customTemplate"],
        message: "A custom naming rule requires a template",
      });
  });
export type SmartCreationReviewNamingRule = z.infer<typeof SmartCreationReviewNamingRuleSchema>;
export const BidSchema = z.number().finite().positive().max(1_000_000);
export function isSmartCreationNegativeKeywordWithinWordLimit(
  text: string,
  matchType: z.infer<typeof NegativeMatchTypeSchema>,
): boolean {
  const words = text.trim().split(/[ \t\n\r\f\v]+/).length;
  return words <= (matchType === "NEGATIVE_PHRASE" ? 4 : 10);
}
export const BudgetSchema = z.number().finite().positive().max(1_000_000);
const BasisPointsSchema = z.number().int().min(1).max(10_000);
export const PlacementPercentageSchema = z.number().int().min(0).max(900);

export const SmartCreationReviewPlanRefSchema = z
  .object({
    kind: z.literal("plan"),
    itemKey: z.null(),
    lineageKey: z.literal("plan"),
  })
  .strict();
export const SmartCreationReviewCampaignRefSchema = z
  .object({
    kind: z.literal("campaign"),
    itemKey: SmartCreationCampaignStableKeySchema,
    lineageKey: LineageKeySchema,
  })
  .strict();
export const SmartCreationReviewAdGroupRefSchema = z
  .object({
    kind: z.literal("ad_group"),
    itemKey: SmartCreationAdGroupStableKeySchema,
    lineageKey: LineageKeySchema,
  })
  .strict();
export const SmartCreationReviewTargetRefSchema = z
  .object({
    kind: z.literal("target"),
    itemKey: SmartCreationTargetStableKeySchema.nullable(),
    lineageKey: LineageKeySchema,
  })
  .strict();
export const SmartCreationReviewNegativeKeywordRefSchema = z
  .object({
    kind: z.literal("negative_keyword"),
    itemKey: z.null(),
    lineageKey: LineageKeySchema,
  })
  .strict();
export const SmartCreationReviewNegativeProductRefSchema = z
  .object({
    kind: z.literal("negative_product"),
    itemKey: z.null(),
    lineageKey: LineageKeySchema,
  })
  .strict();

export const SmartCreationReviewEntityRefSchema = z.discriminatedUnion("kind", [
  SmartCreationReviewPlanRefSchema,
  SmartCreationReviewCampaignRefSchema,
  SmartCreationReviewAdGroupRefSchema,
  SmartCreationReviewTargetRefSchema,
  SmartCreationReviewNegativeKeywordRefSchema,
  SmartCreationReviewNegativeProductRefSchema,
]);
export type SmartCreationReviewEntityRef = z.infer<typeof SmartCreationReviewEntityRefSchema>;

export const SmartCreationReviewSuggestedNumericRangeSchema = z
  .object({
    min: z.number().finite().nonnegative(),
    max: z.number().finite().positive(),
  })
  .strict()
  .refine((range) => range.min <= range.max, { message: "min must not exceed max" });

export function auditedValueSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.object({ effective: valueSchema }).strict();
}

export const SmartCreationReviewBudgetAllocationSchema = z.discriminatedUnion("basis", [
  z.object({ basis: z.literal("amount"), amount: BudgetSchema }).strict(),
  z.object({ basis: z.literal("share"), basisPoints: BasisPointsSchema }).strict(),
]);
export type SmartCreationReviewBudgetAllocation = z.infer<
  typeof SmartCreationReviewBudgetAllocationSchema
>;
